import type IdsDataGridFilters from './ids-data-grid-filters';

/**
 * IDS Data Grid Filter Manager
 * Singleton class that manages all active data grid filter instances across the application.
 * Provides centralized control for closing filter popups and managing filter lifecycle.
 */
export default class IdsDataGridFilterManager {
  /**
   * Singleton instance of the filter manager
   * @private
   */
  static #instance?: IdsDataGridFilterManager;

  /**
   * Collection of active filter instances
   * @private
   */
  #filters: Set<IdsDataGridFilters> = new Set<IdsDataGridFilters>();

  /**
   * Gets the singleton instance of the filter manager
   * @returns {IdsDataGridFilterManager} The filter manager instance
   */
  static getInstance(): IdsDataGridFilterManager {
    if (!this.#instance) {
      this.#instance = new IdsDataGridFilterManager();
    }

    return this.#instance;
  }

  /**
   * Registers a filter instance with the manager
   * @param {IdsDataGridFilters} filter The filter instance to add
   * @returns {void}
   */
  addFilter(filter: IdsDataGridFilters) {
    this.#filters.add(filter);
  }

  /**
   * Clears all registered filter instances
   * @returns {void}
   */
  clearFilter() {
    this.#filters.clear();
  }

  /**
   * Removes a specific filter instance from the manager
   * @param {IdsDataGridFilters} filter The filter instance to remove
   * @returns {void}
   */
  removeFilter(filter: IdsDataGridFilters) {
    const doesExists = this.#filters.has(filter);

    if (doesExists) {
      this.#filters.delete(filter);
    }
  }

  /**
   * Closes all open filter popups across all registered filter instances
   * @returns {void}
   */
  closeAllFilters() {
    this.#filters.forEach((filter) => {
      filter.closeAllFilterPopups();
    });
  }
}
